import { z } from 'zod';

/**
 * Canonical screen identifiers. kebab-case, used directly as deeplink URL paths.
 * Documented exception to ADR-017 snake_case default — primary surface is a URL.
 */
export const screenIdSchema = z.enum([
  'home',
  'updates/required',
  'chat',
  'chat-choice',
  'guided-conversations-intro',
  'guided-conversations-select',
  'profile',
  'profile-showcase',
  'care-specialist',
  'care-team-chat',
  'care-upcoming-calls',
  'care-call-details',
  'care-previous-calls',
  'care-team-introduction',
  'care-first-call-booking',
  'hdhp-notice',
  'care-hdhp-call-booking',
  'care-call-matched',
  'medication',
  'medication-cravings',
  'medication-cravings-product',
  'greeting',
  'login',
  'download',
  'signup',
  'signup/context',
  'signup/substance',
  'signup/legal-name',
  'signup/account',
  'signup/code',
  'signup/plan-details',
  'signup/eligibility-check',
  'signup/employer-search',
  'signup/address',
  'signup/birth-date',
  'signup/contact',
  'signup/quiz-motivation',
  'signup/quiz-goal',
  'signup/baseline-week',
  'signup/baseline-typical',
  'signup/baseline-adjust',
  'signup/screening',
  'signup/wrap-up',
  'signup/hdhp-notice',
  'signup/hdhp-booking',
  'signup/rewards-intro',
  'signup/rewards-points',
  'signup/call-choice',
  'signup/language-preference',
  'signup/booking',
  'signup/booking-unavailable',
  'signup/download',
  'signup/under-age',
  'signup/minor-unsupported',
  'signup/registered',
  'signup/error',
  'signup/book-a-call',
  'signup/login',
  'signup/login-code',
  'signup/login-no-progress',
  'signup/sso-contact',
  'signup/sso-code',
  'signup/terms-of-use',
  'signup/privacy-policy',
  'signup/notice-of-privacy-practices',
  'signup/consumer-health-data-privacy-notice',
  'signup/ai-notice',
  'signup/tcpa-authorization',
  'onboarding-start',
  'onboarding-name',
  'onboarding-motivation',
  'onboarding-substance',
  'onboarding-condition',
  'onboarding-goal',
  'onboarding-change-history',
  'onboarding-cutback-degree',
  'onboarding-sona-intro',
  'app-setup',
  'registered-summary',
  'onboarding-weekly-usage',
  'onboarding-week-summary',
  'onboarding-typical-usage',
  'onboarding-taps2',
  'onboarding-summary',
  'onboarding-rewards-intro',
  'onboarding-conversation-mode',
  'intake-agent',
  'intake-ai-consent',
  'intake-withdrawal-check',
  'sona-state-notice',
  'post-intake-feedback',
  'post-session-feedback',
  'quit-date-picker',
  'cbt-module',
  'intake-plan',
  'initial-review-intro',
  'initial-review-activity',
  'initial-review-check-ins',
  'eligibility-confirm',
  'eligibility-employer-search',
  'eligibility-benefit-holder',
  'eligibility-name',
  'eligibility-contact',
  'eligibility-address',
  'eligibility-sex-at-birth',
  'eligibility-no-match',
  'saml',
  'saml-contact',
  'component-gallery',
  'consent-pending-hold',
  'eligibility-under-age',
  'eligibility-minor-blocked',
  'assessment-showcase',
  'assessment-start',
  'assessment-phq2-q1',
  'assessment-phq2-q2',
  'assessment-gad2-q1',
  'assessment-gad2-q2',
  'assessment-phq9-q3',
  'assessment-phq9-q4',
  'assessment-phq9-q5',
  'assessment-phq9-q6',
  'assessment-phq9-q7',
  'assessment-phq9-q8',
  'assessment-phq9-q9',
  'assessment-gad7-q3',
  'assessment-gad7-q4',
  'assessment-gad7-q5',
  'assessment-gad7-q6',
  'assessment-gad7-q7',
  'assessment-session-suicide-screen',
  'assessment-complete',
  'assessment-result-no-mild',
  'assessment-result-moderate',
  'assessment-result-severe',
  'assessment-result-suicide-risk',
  'rewards',
  'rewards-history',
  'points-history',
  'redemption-history',
  'gift-cards',
  'gift-card-redeem',
  'gift-card-confirm',
  'gift-card-success',
  'gift-items',
  'gift-item-detail',
  'gift-item-confirm',
  'settings',
  'settings-notifications',
  'settings-my-data',
  'settings-my-data-privacy',
  'settings-legal',
  'settings-legal-privacy',
  'settings-legal-terms',
  'settings-legal-consumer-health',
  'settings-legal-privacy-practices',
  'weekly-plan-suggestions',
  'dev-developer-options',
  'dev-analytics-evidence',
  'dev-member-details',
  'dev-screen-preview',
  'dev-flag-override',
  'showcase-agent-launch',
  'debug-network',
  'telemedicine-call',
  'telemedicine-call-rating',
  'telemedicine-call-summary',
  'telemedicine-call-celebration',
  'cab-onboarding',
  'sona-transfer',
  'migration-handoff',
  'migration-registration-consent',
  'migration-welcome',
  'migration-left-off',
  'migration-meet-sona',
  'migration-voice',
  'migration-ai-consent',
  'migration-preparing',
  'migration-text-preview',
  'migration-gap-program',
  'migration-gap-goal',
  'migration-gap-motivation',
  'cab-context-confirm',
  'auth-page',
  'members-list',
  'today-view',
  'member-details',
  'escalation-link',
  'care-outreach-queue',
  'escalation-review',
  'eligibility-review',
  'member-detail-changes',
  'member-lookup',
  'clinicians-list',
  'clinician-details',
  'staff-profile',
  'call-staffing',
  'scheduler-links',
  'telemedicine-appointments',
  'bam-home',
  'bam-questionnaire',
  'not-found',
]);

export type ScreenId = z.infer<typeof screenIdSchema>;

export type Platform = 'mobile' | 'web' | 'atlas';

export type ScreenRegistryEntry = {
  displayName: string;
  platforms: readonly Platform[];
};

const screenRegistryEntries = {
  'updates/required': { displayName: 'Update required', platforms: ['mobile'] },
  home: {
    displayName: 'Home',
    platforms: ['mobile'],
  },
  chat: {
    displayName: 'Chat',
    platforms: ['mobile'],
  },
  'chat-choice': {
    displayName: 'Chat Choice',
    platforms: ['mobile'],
  },
  'guided-conversations-intro': {
    displayName: 'Guided Conversations Intro',
    platforms: ['mobile'],
  },
  'guided-conversations-select': {
    displayName: 'Choose a Conversation',
    platforms: ['mobile'],
  },
  profile: {
    displayName: 'Profile',
    platforms: ['mobile'],
  },
  'profile-showcase': {
    displayName: 'Profile Showcase',
    platforms: ['mobile'],
  },
  'care-specialist': {
    displayName: 'Care Specialist',
    platforms: ['mobile'],
  },
  'care-team-chat': {
    displayName: 'Care Team Chat',
    platforms: ['mobile'],
  },
  'care-upcoming-calls': {
    displayName: 'Upcoming Calls',
    platforms: ['mobile'],
  },
  'care-call-details': {
    displayName: 'Call Details',
    platforms: ['mobile'],
  },
  'care-previous-calls': {
    displayName: 'Previous Calls',
    platforms: ['mobile'],
  },
  'care-team-introduction': {
    displayName: 'Care Team Introduction',
    platforms: ['mobile'],
  },
  'care-first-call-booking': {
    displayName: 'Care: Book Your First Call',
    platforms: ['mobile'],
  },
  'hdhp-notice': {
    displayName: 'Cost-Share Notice',
    platforms: ['mobile'],
  },
  'care-hdhp-call-booking': {
    displayName: 'Care: Book Your Cost Call',
    platforms: ['mobile'],
  },
  'care-call-matched': {
    displayName: 'Care: Call Matched',
    platforms: ['mobile'],
  },
  medication: {
    displayName: 'Medication',
    platforms: ['mobile'],
  },
  'medication-cravings': {
    displayName: 'Medication: Manage Cravings',
    platforms: ['mobile'],
  },
  'medication-cravings-product': {
    displayName: 'Medication: Craving Support Product',
    platforms: ['mobile'],
  },
  greeting: {
    displayName: 'Greeting',
    platforms: ['web'],
  },
  login: {
    displayName: 'Login',
    platforms: ['mobile'],
  },
  download: {
    displayName: 'Registration: Download the App',
    platforms: ['web'],
  },
  signup: {
    displayName: 'Signup: Entry',
    platforms: ['web'] as const,
  },
  'signup/context': {
    displayName: 'Signup: Context',
    platforms: ['web'] as const,
  },
  'signup/substance': {
    displayName: 'Signup: Substance',
    platforms: ['web'] as const,
  },
  'signup/legal-name': {
    displayName: 'Signup: Legal Name',
    platforms: ['web'] as const,
  },
  'signup/account': {
    displayName: 'Signup: Create Account',
    platforms: ['web'] as const,
  },
  'signup/code': {
    displayName: 'Signup: Verification Code',
    platforms: ['web'] as const,
  },
  'signup/plan-details': {
    displayName: 'Signup: Health Plan Details',
    platforms: ['web'] as const,
  },
  'signup/eligibility-check': {
    displayName: 'Signup: Eligibility Check',
    platforms: ['web'] as const,
  },
  'signup/employer-search': {
    displayName: 'Signup: Employer Search',
    platforms: ['web'] as const,
  },
  'signup/address': {
    displayName: 'Signup: Address',
    platforms: ['web'] as const,
  },
  'signup/birth-date': {
    displayName: 'Signup: Birth Date and Sex at Birth',
    platforms: ['web'] as const,
  },
  'signup/contact': {
    displayName: 'Signup: Contact',
    platforms: ['web'] as const,
  },
  'signup/quiz-motivation': {
    displayName: 'Signup: Quiz motivation',
    platforms: ['web'] as const,
  },
  'signup/quiz-goal': {
    displayName: 'Signup: Quiz goal',
    platforms: ['web'] as const,
  },
  'signup/baseline-week': {
    displayName: 'Signup: Last Week Baseline',
    platforms: ['web'] as const,
  },
  'signup/baseline-typical': {
    displayName: 'Signup: Typical Week Check',
    platforms: ['web'] as const,
  },
  'signup/baseline-adjust': {
    displayName: 'Signup: Adjust Typical Week',
    platforms: ['web'] as const,
  },
  'signup/screening': {
    displayName: 'Signup: Screening questions',
    platforms: ['web'] as const,
  },
  'signup/download': {
    displayName: 'Signup: Download the App',
    platforms: ['web'] as const,
  },
  'signup/error': {
    displayName: 'Signup: Something Went Wrong',
    platforms: ['web'] as const,
  },
  'signup/book-a-call': { displayName: 'Signup: Book a Call', platforms: ['web'] as const },
  'signup/login': { displayName: 'Signup: Login', platforms: ['web'] as const },
  'signup/login-code': { displayName: 'Signup: Login Code', platforms: ['web'] as const },
  'signup/login-no-progress': {
    displayName: 'Signup: Login No Progress',
    platforms: ['web'] as const,
  },
  'signup/sso-contact': { displayName: 'Signup: SSO Contact', platforms: ['web'] as const },
  'signup/sso-code': { displayName: 'Signup: SSO Code', platforms: ['web'] as const },
  'signup/wrap-up': {
    displayName: 'Signup: Wrap-up Router',
    platforms: ['web'] as const,
  },
  'signup/hdhp-notice': {
    displayName: 'Signup: High-Deductible Plan Notice',
    platforms: ['web'] as const,
  },
  'signup/hdhp-booking': {
    displayName: 'Signup: Book the Cost Call',
    platforms: ['web'] as const,
  },
  'signup/rewards-intro': {
    displayName: 'Signup: Rewards Intro',
    platforms: ['web'] as const,
  },
  'signup/rewards-points': {
    displayName: 'Signup: Rewards Points',
    platforms: ['web'] as const,
  },
  'signup/call-choice': {
    displayName: 'Signup: Call or Chat',
    platforms: ['web'] as const,
  },
  'signup/language-preference': {
    displayName: 'Signup: Interpreter Preference',
    platforms: ['web'] as const,
  },
  'signup/booking': {
    displayName: 'Signup: Book First Call',
    platforms: ['web'] as const,
  },
  'signup/booking-unavailable': {
    displayName: 'Signup: No Booking Times',
    platforms: ['web'] as const,
  },
  'signup/under-age': {
    displayName: 'Signup: Under Age Stop',
    platforms: ['web'] as const,
  },
  'signup/minor-unsupported': {
    displayName: 'Signup: Minor Not Supported Stop',
    platforms: ['web'] as const,
  },
  'signup/registered': {
    displayName: 'Signup: Successfully Registered',
    platforms: ['web'] as const,
  },
  'signup/terms-of-use': {
    displayName: 'Signup: Terms of Use',
    platforms: ['web'] as const,
  },
  'signup/privacy-policy': {
    displayName: 'Signup: Privacy Policy',
    platforms: ['web'] as const,
  },
  'signup/notice-of-privacy-practices': {
    displayName: 'Signup: Notice of Privacy Practices',
    platforms: ['web'] as const,
  },
  'signup/consumer-health-data-privacy-notice': {
    displayName: 'Signup: Consumer Health Data Privacy Notice',
    platforms: ['web'] as const,
  },
  'signup/ai-notice': { displayName: 'Signup: AI Notice', platforms: ['web'] as const },
  'signup/tcpa-authorization': {
    displayName: 'Signup: TCPA Authorization',
    platforms: ['web'] as const,
  },
  'onboarding-start': { displayName: 'Onboarding: Start', platforms: ['mobile'] as const },
  'onboarding-name': { displayName: 'Onboarding: Name', platforms: ['mobile'] as const },
  'onboarding-motivation': {
    displayName: 'Onboarding: Motivation',
    platforms: ['mobile'] as const,
  },
  'onboarding-substance': {
    displayName: 'Onboarding: Substance',
    platforms: ['mobile'] as const,
  },
  'onboarding-condition': {
    displayName: 'Onboarding: Condition',
    platforms: ['mobile'] as const,
  },
  'onboarding-goal': { displayName: 'Onboarding: Goal', platforms: ['mobile'] as const },
  'onboarding-change-history': {
    displayName: 'Onboarding: Change History',
    platforms: ['mobile'] as const,
  },
  'onboarding-cutback-degree': {
    displayName: 'Onboarding: Cutback Degree',
    platforms: ['mobile'] as const,
  },
  'onboarding-sona-intro': {
    displayName: 'Onboarding: Sona Intro',
    platforms: ['mobile'] as const,
  },
  'app-setup': { displayName: 'Post-web app setup', platforms: ['mobile'] as const },
  'registered-summary': {
    displayName: 'Post-web registered summary',
    platforms: ['mobile'] as const,
  },
  'onboarding-weekly-usage': {
    displayName: 'Onboarding: Weekly Usage',
    platforms: ['mobile'] as const,
  },
  'onboarding-week-summary': {
    displayName: 'Onboarding: Week Summary',
    platforms: ['mobile'] as const,
  },
  'onboarding-typical-usage': {
    displayName: 'Onboarding: Typical Usage',
    platforms: ['mobile'] as const,
  },
  'onboarding-taps2': { displayName: 'Onboarding: TAPS 2', platforms: ['mobile'] as const },
  'onboarding-summary': {
    displayName: 'Onboarding: Summary',
    platforms: ['mobile'] as const,
  },
  'onboarding-rewards-intro': {
    displayName: 'Onboarding: Rewards Intro',
    platforms: ['mobile'] as const,
  },
  'onboarding-conversation-mode': {
    displayName: 'Onboarding: Talk or Text',
    platforms: ['mobile'] as const,
  },
  'intake-agent': { displayName: 'Intake: Voice Agent', platforms: ['mobile'] as const },
  'intake-ai-consent': { displayName: 'Intake: AI consent', platforms: ['mobile'] as const },
  'intake-withdrawal-check': {
    displayName: 'Intake: withdrawal check',
    platforms: ['mobile'] as const,
  },
  'sona-state-notice': {
    displayName: 'Intake: Sona not available in your state',
    platforms: ['mobile'] as const,
  },
  'post-intake-feedback': {
    displayName: 'Intake: Feedback',
    platforms: ['mobile'] as const,
  },
  'post-session-feedback': {
    displayName: 'Session: Feedback',
    platforms: ['mobile'] as const,
  },
  'quit-date-picker': { displayName: 'Quit date: Picker', platforms: ['mobile'] as const },
  'cbt-module': { displayName: 'CBT module', platforms: ['mobile'] as const },
  'intake-plan': { displayName: 'Intake: Next steps', platforms: ['mobile'] as const },
  'initial-review-intro': {
    displayName: 'Initial Review: Introduction',
    platforms: ['mobile'] as const,
  },
  'initial-review-activity': {
    displayName: 'Initial Review: Activity',
    platforms: ['mobile'] as const,
  },
  'initial-review-check-ins': {
    displayName: 'Initial Review: Check-ins',
    platforms: ['mobile'] as const,
  },
  'eligibility-confirm': {
    displayName: 'Registration: Confirm Eligibility',
    platforms: ['mobile'] as const,
  },
  'eligibility-employer-search': {
    displayName: 'Registration: Employer Search',
    platforms: ['mobile'] as const,
  },
  'eligibility-benefit-holder': {
    displayName: 'Registration: Benefit Holder',
    platforms: ['mobile'] as const,
  },
  'eligibility-name': { displayName: 'Registration: Name', platforms: ['mobile'] as const },
  'eligibility-contact': {
    displayName: 'Registration: Contact',
    platforms: ['mobile'] as const,
  },
  'eligibility-address': {
    displayName: 'Registration: Address',
    platforms: ['mobile'] as const,
  },
  'eligibility-sex-at-birth': {
    displayName: 'Registration: Sex Assigned at Birth',
    platforms: ['mobile'] as const,
  },
  'eligibility-no-match': {
    displayName: 'Registration: No Match',
    platforms: ['mobile'] as const,
  },
  saml: {
    displayName: 'Registration: Employer SSO Landing',
    platforms: ['web'] as const,
  },
  'saml-contact': {
    displayName: 'Registration: Employer SSO Contact Choice',
    platforms: ['web'] as const,
  },
  'component-gallery': {
    displayName: 'Dev: Web Component Gallery',
    platforms: ['web'] as const,
  },
  'eligibility-under-age': {
    displayName: 'Registration: Under Age',
    platforms: ['mobile'] as const,
  },
  'eligibility-minor-blocked': {
    displayName: 'Registration: Minor Not Supported',
    platforms: ['mobile'] as const,
  },
  'consent-pending-hold': {
    displayName: 'Registration: Consent Pending Hold',
    platforms: ['mobile'] as const,
  },
  'assessment-showcase': { displayName: 'Assessment Showcase', platforms: ['mobile'] as const },
  'assessment-start': { displayName: 'Assessment: Start', platforms: ['mobile'] as const },
  'assessment-phq2-q1': { displayName: 'Assessment: PHQ-2 Question 1', platforms: ['mobile'] },
  'assessment-phq2-q2': { displayName: 'Assessment: PHQ-2 Question 2', platforms: ['mobile'] },
  'assessment-gad2-q1': { displayName: 'Assessment: GAD-2 Question 1', platforms: ['mobile'] },
  'assessment-gad2-q2': { displayName: 'Assessment: GAD-2 Question 2', platforms: ['mobile'] },
  'assessment-phq9-q3': { displayName: 'Assessment: PHQ-9 Question 3', platforms: ['mobile'] },
  'assessment-phq9-q4': { displayName: 'Assessment: PHQ-9 Question 4', platforms: ['mobile'] },
  'assessment-phq9-q5': { displayName: 'Assessment: PHQ-9 Question 5', platforms: ['mobile'] },
  'assessment-phq9-q6': { displayName: 'Assessment: PHQ-9 Question 6', platforms: ['mobile'] },
  'assessment-phq9-q7': { displayName: 'Assessment: PHQ-9 Question 7', platforms: ['mobile'] },
  'assessment-phq9-q8': { displayName: 'Assessment: PHQ-9 Question 8', platforms: ['mobile'] },
  'assessment-phq9-q9': { displayName: 'Assessment: PHQ-9 Question 9', platforms: ['mobile'] },
  'assessment-gad7-q3': { displayName: 'Assessment: GAD-7 Question 3', platforms: ['mobile'] },
  'assessment-gad7-q4': { displayName: 'Assessment: GAD-7 Question 4', platforms: ['mobile'] },
  'assessment-gad7-q5': { displayName: 'Assessment: GAD-7 Question 5', platforms: ['mobile'] },
  'assessment-gad7-q6': { displayName: 'Assessment: GAD-7 Question 6', platforms: ['mobile'] },
  'assessment-gad7-q7': { displayName: 'Assessment: GAD-7 Question 7', platforms: ['mobile'] },
  'assessment-session-suicide-screen': {
    displayName: 'Assessment: Session Suicide Screen',
    platforms: ['mobile'],
  },
  'assessment-complete': { displayName: 'Assessment: Complete', platforms: ['mobile'] },
  'assessment-result-no-mild': {
    displayName: 'Assessment Result: No/Mild',
    platforms: ['mobile'],
  },
  'assessment-result-moderate': {
    displayName: 'Assessment Result: Moderate',
    platforms: ['mobile'],
  },
  'assessment-result-severe': {
    displayName: 'Assessment Result: Severe',
    platforms: ['mobile'],
  },
  'assessment-result-suicide-risk': {
    displayName: 'Assessment Result: Safety',
    platforms: ['mobile'],
  },
  rewards: { displayName: 'Rewards', platforms: ['mobile'] as const },
  'rewards-history': { displayName: 'Rewards History', platforms: ['mobile'] as const },
  'points-history': { displayName: 'Points History', platforms: ['mobile'] as const },
  'redemption-history': { displayName: 'Redemption History', platforms: ['mobile'] as const },
  'gift-cards': { displayName: 'Gift Cards', platforms: ['mobile'] as const },
  'gift-card-redeem': { displayName: 'Gift Card Redeem', platforms: ['mobile'] as const },
  'gift-card-confirm': { displayName: 'Gift Card Confirm', platforms: ['mobile'] as const },
  'gift-card-success': { displayName: 'Gift Card Success', platforms: ['mobile'] as const },
  'gift-items': { displayName: 'Gift Items', platforms: ['mobile'] as const },
  'gift-item-detail': { displayName: 'Gift Item Detail', platforms: ['mobile'] as const },
  'gift-item-confirm': { displayName: 'Gift Item Confirm', platforms: ['mobile'] as const },
  settings: { displayName: 'Settings', platforms: ['mobile'] as const },
  'settings-notifications': {
    displayName: 'Settings: Notifications',
    platforms: ['mobile'] as const,
  },
  'settings-my-data': { displayName: 'Settings: My Data', platforms: ['mobile'] as const },
  'settings-my-data-privacy': {
    displayName: 'Settings: My Data — Privacy Policy',
    platforms: ['mobile'] as const,
  },
  'settings-legal': { displayName: 'Settings: Legal', platforms: ['mobile'] as const },
  'settings-legal-privacy': {
    displayName: 'Settings: Legal — Privacy Policy',
    platforms: ['mobile'] as const,
  },
  'settings-legal-terms': {
    displayName: 'Settings: Legal — Terms of Use',
    platforms: ['mobile'] as const,
  },
  'settings-legal-consumer-health': {
    displayName: 'Settings: Legal — Consumer Health Data Privacy Notice',
    platforms: ['mobile'] as const,
  },
  'settings-legal-privacy-practices': {
    displayName: 'Settings: Legal — Notice of Privacy Practices',
    platforms: ['mobile'] as const,
  },
  'weekly-plan-suggestions': {
    displayName: 'Weekly Plan: Topic Suggestions',
    platforms: ['mobile'] as const,
  },
  'dev-developer-options': {
    displayName: 'Dev: Developer Options',
    platforms: ['mobile'] as const,
  },
  'dev-analytics-evidence': {
    displayName: 'Dev: Analytics Evidence',
    platforms: ['mobile'] as const,
  },
  'dev-member-details': {
    displayName: 'Dev: Member Details',
    platforms: ['mobile'] as const,
  },
  'dev-screen-preview': {
    displayName: 'Dev: Screen Preview',
    platforms: ['mobile'] as const,
  },
  'dev-flag-override': {
    displayName: 'Dev: Flag Override',
    platforms: ['mobile'] as const,
  },
  'showcase-agent-launch': {
    displayName: 'Showcase: Agent Launch',
    platforms: ['mobile'] as const,
  },
  'debug-network': {
    displayName: 'Dev: Debug Network Simulation',
    platforms: ['mobile'] as const,
  },
  'telemedicine-call': {
    displayName: 'Telemedicine Call',
    platforms: ['mobile'] as const,
  },
  'telemedicine-call-rating': {
    displayName: 'Telemedicine Call Rating',
    platforms: ['mobile'] as const,
  },
  'telemedicine-call-summary': {
    displayName: 'Telemedicine Call Summary',
    platforms: ['mobile'] as const,
  },
  'telemedicine-call-celebration': {
    displayName: 'Telemedicine Call Celebration',
    platforms: ['mobile'] as const,
  },
  'cab-onboarding': { displayName: 'CAB Onboarding', platforms: ['mobile'] as const },
  'sona-transfer': { displayName: 'Sona Handoff Transfer', platforms: ['mobile'] as const },
  'migration-handoff': { displayName: 'Migration Silent Handoff', platforms: ['mobile'] as const },
  'migration-registration-consent': {
    displayName: 'Migration Registration Consent',
    platforms: ['mobile'] as const,
  },
  'migration-welcome': { displayName: 'Migration: Welcome Videos', platforms: ['mobile'] as const },
  'migration-left-off': {
    displayName: 'Migration: Where You Left Off',
    platforms: ['mobile'] as const,
  },
  'migration-meet-sona': {
    displayName: 'Migration: Meet Sona',
    platforms: ['mobile'] as const,
  },
  'migration-voice': {
    displayName: 'Migration: Voice Selection',
    platforms: ['mobile'] as const,
  },
  'migration-ai-consent': {
    displayName: 'Migration: AI Consent',
    platforms: ['mobile'] as const,
  },
  'migration-preparing': {
    displayName: 'Migration: Preparing Sona',
    platforms: ['mobile'] as const,
  },
  'migration-text-preview': {
    displayName: 'Migration: Text Interaction Preview',
    platforms: ['mobile'] as const,
  },
  'migration-gap-program': {
    displayName: 'Migration: Program Gap Collection',
    platforms: ['mobile'] as const,
  },
  'migration-gap-goal': {
    displayName: 'Migration: Goal Gap Collection',
    platforms: ['mobile'] as const,
  },
  'migration-gap-motivation': {
    displayName: 'Migration: Motivation Gap Collection',
    platforms: ['mobile'] as const,
  },
  'cab-context-confirm': {
    displayName: 'CAB Context Confirmation',
    platforms: ['mobile'] as const,
  },
  'auth-page': { displayName: 'Atlas: Authentication', platforms: ['atlas'] },
  'members-list': { displayName: 'Atlas: Members', platforms: ['atlas'] },
  'today-view': { displayName: 'Atlas: Today', platforms: ['atlas'] },
  'member-details': { displayName: 'Atlas: Member Details', platforms: ['atlas'] },
  'escalation-link': { displayName: 'Atlas: Escalation Link', platforms: ['atlas'] },
  'care-outreach-queue': { displayName: 'Atlas: Care Outreach Queue', platforms: ['atlas'] },
  'escalation-review': { displayName: 'Atlas: Escalation Review', platforms: ['atlas'] },
  'eligibility-review': { displayName: 'Atlas: Eligibility Review', platforms: ['atlas'] },
  'member-detail-changes': {
    displayName: 'Atlas: Member Detail Changes',
    platforms: ['atlas'],
  },
  'member-lookup': { displayName: 'Atlas: Member Lookup', platforms: ['atlas'] },
  'clinicians-list': { displayName: 'Atlas: Clinicians', platforms: ['atlas'] },
  'clinician-details': { displayName: 'Atlas: Clinician Details', platforms: ['atlas'] },
  'staff-profile': { displayName: 'Atlas: My Profile', platforms: ['atlas'] },
  'call-staffing': { displayName: 'Atlas: Call Staffing', platforms: ['atlas'] },
  'scheduler-links': { displayName: 'Atlas: Scheduler Links', platforms: ['atlas'] },
  'telemedicine-appointments': {
    displayName: 'Atlas: Telemedicine Appointments',
    platforms: ['atlas'],
  },
  'bam-home': { displayName: 'BAM: Home Card Preview', platforms: ['mobile'] },
  'bam-questionnaire': { displayName: 'BAM: Questionnaire', platforms: ['mobile'] },
  'not-found': { displayName: 'Not Found', platforms: ['atlas'] },
} as const satisfies Record<ScreenId, ScreenRegistryEntry>;

export const screenRegistry: Record<ScreenId, ScreenRegistryEntry> = screenRegistryEntries;

/** Ids whose registry entry lists `platform`, in `ids` order. */
export function screenIdsForPlatform<Id extends string>(
  ids: readonly Id[],
  registry: Record<Id, ScreenRegistryEntry>,
  platform: Platform,
): Id[] {
  return ids.filter((id) => registry[id].platforms.includes(platform));
}

/** Read from the registry's literal `platforms`, so a web- or Atlas-only entry is never one. */
export type MobileScreenId = {
  [Id in ScreenId]: 'mobile' extends (typeof screenRegistryEntries)[Id]['platforms'][number]
    ? Id
    : never;
}[ScreenId];

function isMobileScreenId(id: ScreenId): id is MobileScreenId {
  return screenRegistry[id].platforms.includes('mobile');
}

/** Every screen the mobile app shows, in `screenIdSchema` order. */
export const MOBILE_SCREEN_IDS: readonly MobileScreenId[] =
  screenIdSchema.options.filter(isMobileScreenId);

export const mobileScreenIdSchema = z.enum(MOBILE_SCREEN_IDS);
